// Hive layer for the RIPP Core: circuit highways into the ring, agents travelling with light trails,
// arrivals that deepen the core's breath, and example alert cards popping at circuit junctions.
const HIVE = ["34,211,238", "52,211,153", "251,191,36", "251,146,60", "167,139,250"];
const GOLD = "245,196,81", WHITE = "236,244,255";
const ALERTS = [
  ["NEW LAUNCH", 0], ["FAST MOVER", 3], ["WALLET FLOW", 2], ["X SPIKE", 0], ["RISK CLEAR", 1],
  ["LIQUIDITY OK", 1], ["COPY SIGNAL", 2], ["TARGET HIT", 1], ["VOLUME WAKING", 4],
];

const polar = (R, r, a) => [R + Math.cos(a) * r, R + Math.sin(a) * r];

function buildPaths(R, n, reach) {
  const out = [];
  for (let k = 0; k < n; k++) {
    const a = (k / n) * Math.PI * 2 + 0.12, d = (k % 2 ? 1 : -1) * (0.12 + (k % 3) * 0.05), r0 = reach * (0.82 + (k % 4) * 0.06);
    const pts = [polar(R, r0, a), polar(R, R * 1.32, a), polar(R, R * 1.08, a + d), polar(R, R * 0.84, a + d), polar(R, R * 0.58, a + d)];
    const seg = [];
    let len = 0;
    for (let i = 1; i < pts.length; i++) { const l = Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]); seg.push(l); len += l; }
    out.push({ pts, seg, len, junction: pts[1 + (k % 2)], heat: 0 });
  }
  return out;
}

function at(p, s) {
  let d = Math.max(0, Math.min(1, s)) * p.len;
  for (let i = 0; i < p.seg.length; i++) {
    if (d <= p.seg[i] || i === p.seg.length - 1) {
      const f = Math.min(1, d / p.seg[i]), a = p.pts[i], b = p.pts[i + 1];
      return [a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f];
    }
    d -= p.seg[i];
  }
  return p.pts[p.pts.length - 1];
}

const roundRect = (ctx, x, y, w, h, r) => {
  ctx.beginPath(); ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.closePath();
};

export function makeHive(R, size, reduced, off = 0) {
  const reach = R + off;
  const paths = buildPaths(R, 16, reach);
  const orbs = Array.from({ length: reduced ? 6 : 16 }, (_, i) => ({ p: i % paths.length, s: Math.random(), v: 0.11 + Math.random() * 0.1, c: HIVE[i % HIVE.length] }));
  const cards = [], flares = [];
  const st = { kick: 0, gold: 0, nextCard: 0.6 };

  const arrive = (o, t) => {
    st.kick = Math.min(1.6, st.kick + 0.55); st.gold = Math.min(1, st.gold + 0.6);
    flares.push({ born: t, c: o.c });
    paths[o.p].heat = 1;
    o.p = (o.p + 7) % paths.length; o.s = 0; o.v = 0.11 + Math.random() * 0.1;
  };

  const back = (ctx, t, dt, speed, glow) => {
    st.kick *= 0.94; st.gold *= 0.95;
    const fade = ctx.createRadialGradient(R, R, R * 0.55, R, R, reach);
    fade.addColorStop(0, `rgba(56,189,248,${0.55 * glow})`); fade.addColorStop(0.6, `rgba(56,189,248,${0.3 * glow})`); fade.addColorStop(1, "rgba(56,189,248,0)");
    paths.forEach((p) => {                                            // circuit traces + vias, fading out toward the edge
      p.heat *= 0.96;
      ctx.beginPath(); p.pts.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
      ctx.lineJoin = "round"; ctx.strokeStyle = fade; ctx.lineWidth = 1.1; ctx.stroke();
      if (p.heat > 0.05) { ctx.strokeStyle = `rgba(125,211,252,${0.6 * p.heat * glow})`; ctx.lineWidth = 1 + p.heat * 2; ctx.stroke(); }
      p.pts.slice(1, 4).forEach(([x, y]) => { ctx.fillStyle = `rgba(56,189,248,${(0.3 + 0.5 * p.heat) * glow})`; ctx.beginPath(); ctx.arc(x, y, Math.max(1.4, R * 0.012), 0, Math.PI * 2); ctx.fill(); });
    });
    if (speed <= 0.01) return;
    orbs.forEach((o) => {                                             // agents with light trails
      o.s += dt * o.v * (0.5 + speed * 0.8);
      if (o.s >= 1) { arrive(o, t); return; }
      const p = paths[o.p], trail = reduced ? 0 : 9;
      for (let j = trail; j >= 0; j--) {
        const [x, y] = at(p, o.s - j * 0.018), a = (1 - j / (trail + 1)) * glow;
        ctx.fillStyle = `rgba(${o.c},${0.5 * a})`; ctx.beginPath(); ctx.arc(x, y, R * (0.018 - j * 0.0012), 0, Math.PI * 2); ctx.fill();
      }
      const [x, y] = at(p, o.s), g = ctx.createRadialGradient(x, y, 0, x, y, R * 0.06);
      g.addColorStop(0, `rgba(${WHITE},0.95)`); g.addColorStop(0.3, `rgba(${o.c},0.85)`); g.addColorStop(1, `rgba(${o.c},0)`);
      ctx.fillStyle = g; ctx.fillRect(x - R * 0.06, y - R * 0.06, R * 0.12, R * 0.12);
    });
    if (t > st.nextCard) {                                            // example alert pops at a junction
      const p = paths[Math.floor(Math.random() * paths.length)], [label, ci] = ALERTS[Math.floor(Math.random() * ALERTS.length)];
      cards.push({ born: t, x: p.junction[0], y: p.junction[1], label, c: HIVE[ci] });
      p.heat = 1; st.nextCard = t + (reduced ? 2.4 : 1.1 + Math.random() * 0.8);
      if (cards.length > 3) cards.shift();
    }
  };

  const front = (ctx, t, glow) => {
    if (st.gold > 0.02) {                                             // golden crown glow at the heart
      const g = ctx.createRadialGradient(R, R, 0, R, R, R * 0.34);
      g.addColorStop(0, `rgba(${GOLD},${0.45 * st.gold * glow})`); g.addColorStop(1, `rgba(${GOLD},0)`);
      ctx.save(); ctx.globalCompositeOperation = "lighter"; ctx.fillStyle = g; ctx.fillRect(R - R * 0.34, R - R * 0.34, R * 0.68, R * 0.68); ctx.restore();
    }
    for (let i = flares.length - 1; i >= 0; i--) {                     // arrival flare ring
      const f = flares[i], age = (t - f.born) / 0.9;
      if (age >= 1) { flares.splice(i, 1); continue; }
      ctx.strokeStyle = `rgba(${f.c},${(1 - age) * 0.8})`; ctx.lineWidth = 2.2 * (1 - age) + 0.4;
      ctx.beginPath(); ctx.arc(R, R, R * (0.55 + age * 0.22), 0, Math.PI * 2); ctx.stroke();
    }
    const fs = Math.max(8.5, R * 0.058);
    ctx.font = `700 ${fs}px "JetBrains Mono", monospace`; ctx.textBaseline = "middle";
    cards.forEach((c) => {
      const age = t - c.born, life = 2.1;
      if (age > life) return;
      const pop = Math.min(1, age / 0.18), a = Math.min(1, (life - age) / 0.4) * pop;
      const w = ctx.measureText(c.label).width + fs * 2.4, h = fs * 2.1;
      const x = Math.max(2 - off, Math.min(size + off - w - 2, c.x - w / 2)), y = Math.max(2 - off, c.y - h - R * 0.05 - (1 - pop) * 6);
      ctx.save(); ctx.globalAlpha = a;
      roundRect(ctx, x, y, w, h, h * 0.3); ctx.fillStyle = "rgba(8,12,26,0.82)"; ctx.fill();
      ctx.strokeStyle = `rgba(${c.c},0.9)`; ctx.lineWidth = 1; ctx.stroke();
      ctx.shadowColor = `rgba(${c.c},0.9)`; ctx.shadowBlur = 8;
      ctx.fillStyle = `rgba(${c.c},1)`; ctx.beginPath(); ctx.arc(x + fs * 0.9, y + h / 2, fs * 0.28, 0, Math.PI * 2); ctx.fill();
      ctx.shadowBlur = 0; ctx.fillStyle = `rgba(${WHITE},0.95)`; ctx.fillText(c.label, x + fs * 1.6, y + h / 2 + 0.5);
      ctx.strokeStyle = `rgba(${c.c},0.6)`; ctx.beginPath(); ctx.moveTo(c.x, y + h); ctx.lineTo(c.x, c.y); ctx.stroke();
      ctx.restore();
    });
  };

  return { back, front, get kick() { return st.kick; } };
}
